/**
 * LearnerCourseProgressBar — pill track + percent bubble.
 *
 * Matches Figma 9153:97215. The percent **bubble is fixed** at the right
 * end of the track (overflowing past it by half its width); only the fill
 * animates leftward as progress grows. Both the fill width and the percent
 * label inside the bubble animate from 0 → target on first mount over
 * 900 ms with the project's EASE_OUT curve.
 *
 * Reduced-motion: snaps to the target with no transition.
 */

import React, { useEffect, useState } from 'react';
import { useReducedMotion } from 'framer-motion';

interface LearnerCourseProgressBarProps {
  /** Progress percent (0–100). */
  percent: number;
  /** Optional — used for the SR label (e.g. "3 of 10 modules complete"). */
  modulesCompleted?: number;
  modulesTotal?: number;
  className?: string;
}

const EASE_OUT_CSS = 'cubic-bezier(0.23, 1, 0.32, 1)';
const ANIM_MS = 900;

// Fixed bubble dimensions per Figma (61 × 58). Track reserves
// `BUBBLE_OVERLAP` of right padding so the bubble's center sits on the
// track's right edge while the bubble extends past it visually.
const BUBBLE_W = 61;
const BUBBLE_H = 58;
const BUBBLE_OVERLAP = BUBBLE_W / 2;

export default function LearnerCourseProgressBar({
  percent,
  modulesCompleted,
  modulesTotal,
  className = '',
}: LearnerCourseProgressBarProps) {
  const reduceMotion = useReducedMotion();
  const clamped = Math.max(0, Math.min(100, percent));

  // Animate from 0 → target on first mount. Subsequent percent changes also
  // ride the same CSS transition.
  const [displayPercent, setDisplayPercent] = useState(reduceMotion ? clamped : 0);

  useEffect(() => {
    if (reduceMotion) {
      setDisplayPercent(clamped);
      return;
    }
    // Two RAFs: paint the initial 0 % once, then flip to the target so the
    // CSS transition picks up the change instead of being batched away.
    let raf2 = 0;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        setDisplayPercent(clamped);
      });
    });
    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
    };
  }, [clamped, reduceMotion]);

  const ariaLabel =
    modulesCompleted != null && modulesTotal != null
      ? `${modulesCompleted} of ${modulesTotal} modules complete`
      : `${Math.round(clamped)} percent complete`;

  const transition = reduceMotion ? 'none' : `${ANIM_MS}ms ${EASE_OUT_CSS}`;

  return (
    <div
      role="progressbar"
      aria-valuenow={Math.round(clamped)}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label={ariaLabel}
      className={`relative w-full ${className}`}
      style={{ height: `${BUBBLE_H}px` }}
    >
      {/* Track — ends BUBBLE_OVERLAP before the wrapper's right edge so the
         bubble's center anchors at the track's right end. */}
      <div
        aria-hidden="true"
        className="absolute left-0 top-1/2 -translate-y-1/2 h-[10px] rounded-full overflow-hidden bg-[rgba(217,217,217,0.7)] dark:bg-white/10"
        style={{
          right: `${BUBBLE_OVERLAP}px`,
          boxShadow: '0 0 19.8px rgba(0,0,0,0.12)',
        }}
      >
        {/* Fill */}
        <div
          className="absolute left-0 top-1/2 -translate-y-1/2 h-[9px] rounded-full bg-primary-500"
          style={{
            width: `${displayPercent}%`,
            transition: `width ${transition}`,
            boxShadow: '0 0 19.8px rgba(0,0,0,0.18)',
          }}
        />
      </div>

      {/* Bubble — fixed at the right edge, overflowing past the track. */}
      <div
        aria-hidden="true"
        className="absolute right-0 top-1/2 -translate-y-1/2 inline-flex items-center justify-center rounded-full bg-[rgba(217,217,217,0.95)] dark:bg-white/85"
        style={{
          width: `${BUBBLE_W}px`,
          height: `${BUBBLE_H}px`,
          boxShadow: '0 0 9.9px rgba(0,0,0,0.18)',
        }}
      >
        <span className="text-[13px] font-semibold text-black tabular-nums leading-none">
          {Math.round(displayPercent)}
          <span className="text-[14px]">%</span>
        </span>
      </div>
    </div>
  );
}
